import { parseISO } from 'date-fns';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/system';

import { GameModel } from '../types';

import { getTeamLogoUrl } from '@/shared/services/getTeamLogoUrl';
import { useVenueAwareTimeFormatter } from '../hooks/useVenueAwareTimeFormatter';
import { LocalSessionState, useAuth } from '@/features/scaffolding/components/AuthContext';
import { Tracking } from '@/shared/services/tracking';

type Props = {
  game: GameModel;
};

// Kept in step with GameCard: fills the grid column, fixed height so rows align.
const CARD_HEIGHT = 210;


const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const CelebrationGameCard = ({ game }: Props) => {
  const auth = useAuth();
  const { formatTime } = useVenueAwareTimeFormatter();

  const start = game.startTimestamp instanceof Date
    ? game.startTimestamp
    : parseISO(game.startTimestamp || '');

  const [isHovered, setIsHovered] = useState(false);

  const isClickable = auth.sessionState === LocalSessionState.READY;

  const leagueName = game.season?.league?.name ?? '';
  const championTeam = game.homeTeam;
  const title = game.celebrationName?.trim() || `${leagueName} Celebration`;

  return (
    <Box sx={{ position: 'relative', animation: `${slideUp} 0.8s ease-out` }}>
      {auth.sessionState !== LocalSessionState.READY && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(5px)',
            borderRadius: 1,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 400 }}>
            Loading...
          </Typography>
        </Box>
      )}

      <Link
        to={isClickable ? `/games/${game.uniqueId}` : '#'}
        style={{ textDecoration: 'none' }}
        onClick={(e) => {
          if (!isClickable) e.preventDefault();
          Tracking.getInstance().track('Watch Celebration', {
            category: 'Games',
            game: game.name,
          });
        }}
      >
        <Card
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            width: '100%',
            height: CARD_HEIGHT,
            borderRadius: 1,
            overflow: 'hidden',
            position: 'relative',
            cursor: isClickable ? 'pointer' : 'default',
            pointerEvents: isClickable ? 'auto' : 'none',
            background:
              `linear-gradient(135deg, #1a0f00 0%, #ffd700 30%, #b8870b67 55%, #ffd700 75%, #b8860b 100%)`,
            backgroundSize: '200% 200%',
          //  animation: `${goldShimmer} 6s ease-in-out infinite, ${trophyGlow} 3s ease-in-out infinite`,
            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: isHovered ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
            border: '1px solid rgba(255, 215, 0, 0.6)',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(circle at 20% 0%, rgba(255,255,255,0.25), transparent 40%)',
              pointerEvents: 'none',
            },
          }}
        >
          {/* Hover CTA overlay */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(135deg, rgba(0,0,0,0.25), rgba(0,0,0,0.55))',
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.4s ease',
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(2px)',
            }}
          >
            <Typography
              variant="h6"
              sx={{
           color: 'white',
                  fontWeight: 400,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              }}
            >
              Watch Celebration
            </Typography>
          </Box>

          <CardContent
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              p: 3,
              position: 'relative',
              zIndex: 1,
              '&:last-child': { pb: 3 },
            }}
          >
            {/* Header: date + championship badge */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
      
           
                <Typography
                  variant="caption"
                  sx={{
                    color: '#fff8dc',
                    fontWeight: 700,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    fontSize: '0.65rem',
                  }}
                >
                  Celebration
                </Typography>
              
            </Box>

            {/* Body: team logo + title */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flex: 1,
              }}
            >
              {championTeam && (
                <Box
                  component="img"
                  src={getTeamLogoUrl(championTeam)}
                  alt={championTeam.abbreviation}
                  sx={{
                    width: 72,
                    height: 72,
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.5))',
                    transition: 'transform 0.3s ease',
                    transform: isHovered ? 'scale(1.1) rotate(-4deg)' : 'scale(1) rotate(0deg)',
                  }}
                />
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
               
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: '#fff8dc',
                    fontWeight: 400,
                    textShadow: '0 2px 6px rgba(0,0,0,0.65)',
                    lineHeight: 1.1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={title}
                >
                  {title} Winner
                </Typography>
                {championTeam?.name && (
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'rgba(255, 248, 220, 0.9)',
                      mt: 0.5,
                      fontWeight: 600,
                      textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {championTeam.name}
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Link>
    </Box>
  );
};

export default CelebrationGameCard;
